import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext(null);

// Separate storage keys for completely isolated cart flows
const READY_MADE_CART_KEY = "readyMadeCart";
const CREATE_BRAND_CART_KEY = "createBrandCart";
const ACTIVE_CART_CONTEXT_KEY = "activeCartContext";

// Cart context types
export const CART_CONTEXTS = {
  READY_MADE: "ready_made",
  CREATE_BRAND: "create_brand",
  NONE: null
};

// Load cart from sessionStorage
const loadCart = (key) => {
  if (typeof window === 'undefined') return [];
  const savedCart = sessionStorage.getItem(key);
  if (savedCart) {
    try {
      return JSON.parse(savedCart);
    } catch (e) {
      console.error(`Error loading cart (${key}):`, e);
      return [];
    }
  }
  return [];
};

// Load active context from sessionStorage
const loadActiveContext = () => {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(ACTIVE_CART_CONTEXT_KEY) || null;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // ============================================================
  // TWO INDEPENDENT CART STATES - Never overlap
  // ============================================================
  const [readyMadeCartItems, setReadyMadeCartItems] = useState(() => loadCart(READY_MADE_CART_KEY));
  const [createBrandCartItems, setCreateBrandCartItems] = useState(() => loadCart(CREATE_BRAND_CART_KEY));
  const [activeCartContext, setActiveCartContext] = useState(() => loadActiveContext());

  // Persist Ready-Made cart to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(READY_MADE_CART_KEY, JSON.stringify(readyMadeCartItems));
  }, [readyMadeCartItems]);

  // Persist Create Brand cart to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(CREATE_BRAND_CART_KEY, JSON.stringify(createBrandCartItems));
  }, [createBrandCartItems]);

  // Persist active context to sessionStorage
  useEffect(() => {
    if (activeCartContext) {
      sessionStorage.setItem(ACTIVE_CART_CONTEXT_KEY, activeCartContext);
    } else {
      sessionStorage.removeItem(ACTIVE_CART_CONTEXT_KEY);
    }
  }, [activeCartContext]);

  // ============================================================
  // CONTEXT SWITCHING LOGIC
  // ============================================================
  
  // Check if there's an active cart in the opposite context
  const hasOppositeContextCart = useCallback((targetContext) => {
    if (targetContext === CART_CONTEXTS.READY_MADE) {
      return createBrandCartItems.length > 0;
    }
    if (targetContext === CART_CONTEXTS.CREATE_BRAND) {
      return readyMadeCartItems.length > 0;
    }
    return false;
  }, [readyMadeCartItems, createBrandCartItems]);

  // Switch cart context (only if opposite cart is empty or user confirms)
  const switchCartContext = useCallback((newContext, force = false) => {
    if (!force && hasOppositeContextCart(newContext)) {
      return {
        success: false,
        message: "You have items in another cart. Please complete or clear that cart first.",
        conflictingContext: newContext === CART_CONTEXTS.READY_MADE 
          ? CART_CONTEXTS.CREATE_BRAND 
          : CART_CONTEXTS.READY_MADE
      };
    }
    setActiveCartContext(newContext);
    return { success: true };
  }, [hasOppositeContextCart]);

  // ============================================================
  // READY-MADE BRAND CART FUNCTIONS
  // ============================================================
  
  const addToReadyMadeCart = useCallback((item) => {
    // Ensure we're in the right context
    const contextResult = switchCartContext(CART_CONTEXTS.READY_MADE);
    if (!contextResult.success) {
      return contextResult;
    }

    const cartItem = { 
      ...item, 
      cartContext: CART_CONTEXTS.READY_MADE,
      addedAt: new Date().toISOString()
    };
    
    setReadyMadeCartItems(prev => {
      const existingIndex = prev.findIndex(ci => ci.brand?.id === item.brand?.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = cartItem;
        return updated;
      }
      return [...prev, cartItem];
    });
    
    return { success: true };
  }, [switchCartContext]);

  const removeFromReadyMadeCart = useCallback((brandId) => {
    setReadyMadeCartItems(prev => prev.filter(item => item.brand?.id !== brandId));
  }, []);

  const clearReadyMadeCart = useCallback(() => {
    setReadyMadeCartItems([]);
    if (activeCartContext === CART_CONTEXTS.READY_MADE) {
      setActiveCartContext(null);
    }
    sessionStorage.removeItem(READY_MADE_CART_KEY);
  }, [activeCartContext]);

  const getReadyMadeCartTotal = useCallback(() => {
    return readyMadeCartItems.reduce((sum, item) => sum + (item.total || 0), 0);
  }, [readyMadeCartItems]);

  const getReadyMadeCartCount = useCallback(() => {
    return readyMadeCartItems.length;
  }, [readyMadeCartItems]);

  // ============================================================
  // CREATE BRAND CART FUNCTIONS
  // ============================================================
  
  const addToCreateBrandCart = useCallback((item) => {
    // Ensure we're in the right context
    const contextResult = switchCartContext(CART_CONTEXTS.CREATE_BRAND);
    if (!contextResult.success) {
      return contextResult;
    }

    const cartItem = { 
      ...item, 
      cartContext: CART_CONTEXTS.CREATE_BRAND,
      addedAt: new Date().toISOString()
    };
    
    setCreateBrandCartItems(prev => {
      // For create brand, we typically only have one brand activation at a time
      const existingIndex = prev.findIndex(ci => ci.brand?.brand_id === item.brand?.brand_id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = cartItem;
        return updated;
      }
      return [...prev, cartItem];
    });
    
    return { success: true };
  }, [switchCartContext]);

  const removeFromCreateBrandCart = useCallback((brandId) => {
    setCreateBrandCartItems(prev => prev.filter(item => item.brand?.brand_id !== brandId));
  }, []);

  const clearCreateBrandCart = useCallback(() => {
    setCreateBrandCartItems([]);
    if (activeCartContext === CART_CONTEXTS.CREATE_BRAND) {
      setActiveCartContext(null);
    }
    sessionStorage.removeItem(CREATE_BRAND_CART_KEY);
  }, [activeCartContext]);

  const getCreateBrandCartTotal = useCallback(() => {
    return createBrandCartItems.reduce((sum, item) => sum + (item.total || 0), 0);
  }, [createBrandCartItems]);

  const getCreateBrandCartCount = useCallback(() => {
    return createBrandCartItems.length;
  }, [createBrandCartItems]);

  // ============================================================
  // LOGIN REDIRECT HELPERS
  // ============================================================
  
  const getCheckoutRedirectUrl = useCallback(() => {
    if (activeCartContext === CART_CONTEXTS.READY_MADE) {
      return "/store?checkout=ready-made";
    }
    if (activeCartContext === CART_CONTEXTS.CREATE_BRAND) {
      // For create brand, redirect to the specific brand activation page
      const brandItem = createBrandCartItems[0];
      if (brandItem?.brand?.brand_id) {
        return `/activate-brand/${brandItem.brand.brand_id}`;
      }
      return "/dashboard";
    }
    return "/";
  }, [activeCartContext, createBrandCartItems]);

  const setLoginReturnUrl = useCallback(() => {
    const redirectUrl = getCheckoutRedirectUrl();
    sessionStorage.setItem('checkoutReturnUrl', redirectUrl);
    return redirectUrl;
  }, [getCheckoutRedirectUrl]);

  // ============================================================
  // VALIDATION & ERROR HANDLING
  // ============================================================
  
  const validateCartContext = useCallback(() => {
    // Check if cart context is valid
    if (!activeCartContext) {
      const hasReadyMade = readyMadeCartItems.length > 0;
      const hasCreateBrand = createBrandCartItems.length > 0;
      
      if (hasReadyMade && hasCreateBrand) {
        // Corrupted state - both carts have items but no context
        return {
          valid: false,
          error: "CONTEXT_CORRUPTED",
          message: "We need to confirm what you're purchasing before continuing."
        };
      }
      
      if (hasReadyMade) {
        setActiveCartContext(CART_CONTEXTS.READY_MADE);
        return { valid: true };
      }
      
      if (hasCreateBrand) {
        setActiveCartContext(CART_CONTEXTS.CREATE_BRAND);
        return { valid: true };
      }
      
      // No items in any cart
      return { valid: true };
    }
    
    return { valid: true };
  }, [activeCartContext, readyMadeCartItems, createBrandCartItems]);

  // ============================================================
  // LEGACY SUPPORT (for components using old API)
  // These map to Ready-Made cart for backward compatibility
  // ============================================================
  
  const cartItems = readyMadeCartItems;
  const addToCart = addToReadyMadeCart;
  const removeFromCart = removeFromReadyMadeCart;
  const clearCart = clearReadyMadeCart;
  const getCartTotal = getReadyMadeCartTotal;
  const getCartCount = getReadyMadeCartCount;
  const isInCart = useCallback((brandId) => {
    return readyMadeCartItems.some(item => item.brand?.id === brandId);
  }, [readyMadeCartItems]);

  return (
    <CartContext.Provider
      value={{
        // Active context
        activeCartContext,
        switchCartContext,
        hasOppositeContextCart,
        validateCartContext,
        CART_CONTEXTS,
        
        // Ready-Made Brand cart (Store)
        readyMadeCartItems,
        addToReadyMadeCart,
        removeFromReadyMadeCart,
        clearReadyMadeCart,
        getReadyMadeCartTotal,
        getReadyMadeCartCount,
        
        // Create Brand cart (Activation)
        createBrandCartItems,
        addToCreateBrandCart,
        removeFromCreateBrandCart,
        clearCreateBrandCart,
        getCreateBrandCartTotal,
        getCreateBrandCartCount,
        
        // Login/redirect helpers
        getCheckoutRedirectUrl,
        setLoginReturnUrl,
        
        // Legacy API (maps to Ready-Made cart)
        cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        getCartTotal,
        getCartCount,
        isInCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
